import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { ChevronLeft, ChevronRight, Copy, Check, Code, FileText, Loader2, FileWarning } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MarkdownContent } from '@/components/chat/MarkdownContent';
import { PDFViewer } from '@/components/contacts/PDFViewer';
import { getAttachmentUrl } from '@/services/encompassDocumentService';
import { useToast } from '@/hooks/use-toast';

interface ExtractionTextModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extractedText: string;
  documentTitle?: string | null;
  storagePath?: string | null;
}

interface ParsedPage {
  pageNumber: number;
  content: string;
}

/**
 * Parses extracted text into pages using "--- Page X ---" delimiters
 */
function parsePages(text: string): ParsedPage[] {
  if (!text) {
    return [];
  }

  // Split by page delimiter pattern
  const pagePattern = /--- Page (\d+) ---/g;
  const parts = text.split(pagePattern);

  // If no delimiters found, return as single page
  if (parts.length === 1) {
    return [{ pageNumber: 1, content: text.trim() }];
  }

  const pages: ParsedPage[] = [];

  // parts array alternates: [before, pageNum, content, pageNum, content, ...]
  // Skip the first element if it's empty (text starts with delimiter)
  let i = parts[0].trim() ? 0 : 1;

  while (i < parts.length) {
    if (i === 0 && parts[0].trim()) {
      // Content before first delimiter (shouldn't happen but handle it)
      pages.push({ pageNumber: 0, content: parts[0].trim() });
      i++;
    } else {
      const pageNum = parseInt(parts[i], 10);
      const content = parts[i + 1]?.trim() || '';
      if (!isNaN(pageNum) && content) {
        pages.push({ pageNumber: pageNum, content });
      }
      i += 2;
    }
  }

  return pages;
}

export function ExtractionTextModal({
  open,
  onOpenChange,
  extractedText,
  documentTitle,
  storagePath
}: ExtractionTextModalProps) {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'rendered' | 'raw'>('rendered');
  const [copied, setCopied] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { toast } = useToast();

  // Parse pages from extracted text
  const pages = useMemo(() => parsePages(extractedText), [extractedText]);
  const totalPages = pages.length;
  const currentPage = pages[currentPageIndex];

  // Reset state when modal opens with new content
  useEffect(() => {
    if (open) {
      setCurrentPageIndex(0);
      setPdfUrl(null);
    }
  }, [open, extractedText]);

  // Load PDF URL when modal opens and storagePath is provided
  useEffect(() => {
    if (open && storagePath) {
      setPdfLoading(true);
      getAttachmentUrl(storagePath)
        .then((url) => {
          setPdfUrl(url);
        })
        .catch(() => {
          setPdfUrl(null);
        })
        .finally(() => {
          setPdfLoading(false);
        });
    }
  }, [open, storagePath]);

  const handlePrevPage = useCallback(() => {
    setCurrentPageIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPageIndex((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  const handlePageSelect = useCallback((value: string) => {
    setCurrentPageIndex(parseInt(value, 10));
  }, []);

  const handleCopy = useCallback(() => {
    if (currentPage?.content) {
      navigator.clipboard.writeText(currentPage.content);
      setCopied(true);
      toast({
        title: 'Copied',
        description: `Page ${currentPage.pageNumber} copied to clipboard`
      });
      setTimeout(() => setCopied(false), 2000);
    }
  }, [currentPage, toast]);

  if (!extractedText) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 py-3 border-b flex-shrink-0">
          <DialogTitle className="text-lg font-semibold truncate pr-8">
            {documentTitle || 'Extracted Text'}
          </DialogTitle>
        </DialogHeader>

        {/* Split Pane Content */}
        <PanelGroup direction="horizontal" className="flex-1 min-h-0">
          {/* Left Panel: Extracted Text */}
          <Panel defaultSize={50} minSize={30}>
            <div className="flex flex-col h-full">
              {/* View Mode Toggle */}
              <div className="px-4 py-2 border-b flex items-center gap-2 flex-shrink-0 bg-muted/30">
                <Button
                  variant={viewMode === 'rendered' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('rendered')}
                  className="h-7"
                >
                  <FileText className="h-3.5 w-3.5 mr-1" />
                  Rendered
                </Button>
                <Button
                  variant={viewMode === 'raw' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('raw')}
                  className="h-7"
                >
                  <Code className="h-3.5 w-3.5 mr-1" />
                  Raw
                </Button>
              </div>

              {/* Extracted Text Content */}
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-4">
                  {currentPage ? (
                    viewMode === 'rendered' ? (
                      <MarkdownContent
                        content={currentPage.content}
                        className="prose prose-sm dark:prose-invert max-w-none"
                      />
                    ) : (
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-muted/50 p-4 rounded-lg">
                        {currentPage.content}
                      </pre>
                    )
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      No content available
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Footer with Navigation */}
              <div className="px-4 py-2 border-t flex items-center justify-between flex-shrink-0 bg-muted/30">
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={currentPageIndex === 0}
                    className="h-7 px-2"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Page</span>
                    <Select
                      value={currentPageIndex.toString()}
                      onValueChange={handlePageSelect}
                    >
                      <SelectTrigger className="w-16 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pages.map((page, index) => (
                          <SelectItem key={index} value={index.toString()}>
                            {page.pageNumber}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">
                      of {totalPages}
                    </span>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPageIndex === totalPages - 1}
                    className="h-7 px-2"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className={cn('h-7', copied && 'text-green-600')}
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors cursor-col-resize" />

          {/* Right Panel: PDF Viewer */}
          <Panel defaultSize={50} minSize={30}>
            <div className="h-full flex flex-col bg-muted/30">
              {pdfLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Loading document...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <PDFViewer
                  url={pdfUrl}
                  className="h-full"
                  showBottomControls={false}
                  initialPage={currentPage?.pageNumber || 1}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <FileWarning className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">
                      PDF preview not available
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      The source document could not be loaded
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Panel>
        </PanelGroup>
      </DialogContent>
    </Dialog>
  );
}

export default ExtractionTextModal;
